import { useState, useEffect, useCallback } from 'react';
import { useIntl, FormattedMessage } from 'react-intl';
import { Flex, Container } from '@chakra-ui/react';

import { useDispatch, useSelector } from 'react-redux';

import { setAccountConnection } from '../store/connection/connectionSlice';
import {
    deleteEnvironmentAndBookHeaders,
    getUserConnections,
    setAccountConnectionConfig,
    removeConnectionSearchParams,
} from '../services/connections';
import useConnectionSearch from '../hooks/useConnectionSearch';
import useHistoryPush from '../hooks/useHistoryPush';

import ConnectionStep from '../components/connection/ConnectionStep';
import ConnectionOption from '../components/connection/ConnectionOption';
import { deleteLocalStudyFiltersConfig } from 'services/studies';

const Connection = () => {
    const intl = useIntl();
    const historyPush = useHistoryPush();
    const dispatch = useDispatch();
    const connectionConfig = useSelector((state) => state.connections);

    const [connection, setConnection] = useState({
        isLoading: false,
        options: [],
        error: '',
        value: connectionConfig?.connection,
        title: connectionConfig?.connectionTitle,
    });

    const { search, setSearch, setSearchCollection, noSearchResultsFound, searchResultsFound } = useConnectionSearch();

    const handleSearch = (value) => {
        setSearch((prev) => ({ ...prev, term: value }));
        setSearchCollection(connection.options);
    };

    const getConnections = useCallback(async () => {
        setConnection((prev) => ({ ...prev, isLoading: true }));

        try {
            const data = await getUserConnections();

            setConnection((prev) => ({
                ...prev,
                isLoading: false,
                options: data.map((item) => ({
                    title: item.name,
                    subtitle: '',
                    value: item.id,
                    icon: null,
                })),
            }));
        } catch (err) {
            if (err.response?.status === 500) {
                setConnection((prev) => ({
                    ...prev,
                    isLoading: false,
                    error: intl.formatMessage({ id: 'connection_error_fetch_connections' }),
                }));
            }
        }
    }, [intl]);

    const clearDefaultsConfig = useCallback(async () => {
        // Clear all connection local storage defaults
        setAccountConnectionConfig(null);
        // Reset saved study filters
        deleteLocalStudyFiltersConfig();
        // When on connection page we need to remove environmentId and bookId from axios headers
        deleteEnvironmentAndBookHeaders();

        const connectionConfig = {
            connection: connection?.value,
            connectionTitle: connection?.title,
            environment: null,
            environmentTitle: '',
            book: null,
            bookTitle: '',
            schemaGroupId: null,
            isDefaultConfig: false,
            versionName: '',
        };

        // Clear store, but need to preserve original connection if any
        dispatch(setAccountConnection(connectionConfig));
    }, [connection?.value, connection?.title, dispatch]);

    useEffect(() => {
        clearDefaultsConfig();
        getConnections();
    }, [getConnections, clearDefaultsConfig]);

    const onConnectionSelect = async (selected, selectedTitle) => {
        setConnection((prev) => ({ ...prev, value: selected, title: selectedTitle }));

        //Added to avoid the blank page that appears after encountering an incompatible version
        const clearCurrent = removeConnectionSearchParams(window.location.search);

        const search = new URLSearchParams(clearCurrent);
        search.append('_connectionId', selected);
        search.append('_connectionTitle', selectedTitle);

        historyPush({
            pathname: `/environment`,
            search,
        });
    };

    return (
        <Flex minHeight="100vh" bg="background-secondary" pt={40} pb={20} flexDirection="column">
            <Container maxW={1056}>
                <ConnectionStep
                    onSearch={handleSearch}
                    current={1}
                    step={1}
                    total={3}
                    title={<FormattedMessage id="connection_select_connection" />}
                    subtitle={<FormattedMessage id="connection_connection" />}
                    description={<FormattedMessage id="connection_please_select_the_connection" />}
                    isLoading={connection.isLoading}
                    error={connection.error}
                    noResults={noSearchResultsFound}
                    onErrorDismiss={() => {
                        setConnection((prev) => ({ ...prev, error: '' }));
                        getConnections();
                    }}
                >
                    {(searchResultsFound ? search.results : connection.options).map((o) => (
                        <ConnectionOption
                            {...o}
                            key={o.value}
                            isSelected={Number(connection.value) === o.value}
                            onClick={() => onConnectionSelect(o.value, o.title)}
                        />
                    ))}
                </ConnectionStep>
            </Container>
        </Flex>
    );
};

export default Connection;
